import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParcels, useRecordSource, useRefreshRecord, useAcceptChanges, type Parcel, type FieldChange } from '@/api/hooks';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { GlobeSpinner } from '@/components/ui/globe-spinner';
import { PageBanner } from '@/components/layout/PageBanner';
import { SortableHeader, type SortDir } from '@/components/ui/sortable-header';
import { TablePager } from '@/components/ui/table-pager';
import { FreshnessBadge } from '@/components/ui/freshness-badge';
import { ChangeReport } from '@/components/ui/change-report';
import { formatCurrency } from '@/lib/utils';
import { Search, Fingerprint, Copy, Check, Loader2, X, MapPin, RefreshCw } from 'lucide-react';
import { getTheme } from '@/lib/theme';

/* ── Pretty-print helpers ── */

/** Turn snake_case key into Title Case label */
function formatKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .replace(/\bId\b/g, 'ID')
    .replace(/\bApn\b/g, 'APN')
    .replace(/\bUpi\b/g, 'UPI')
    .replace(/\bFips\b/g, 'FIPS')
    .replace(/\bSqft\b/g, 'Sq Ft')
    .replace(/\bUrl\b/g, 'URL');
}

function formatValue(key: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
      return new Date(value).toLocaleString();
    }
    return value.trim();
  }
  if (typeof value === 'number') {
    if (key.includes('value') || key.includes('assessed') || key.includes('market') || key.includes('price')) {
      return formatCurrency(value);
    }
    if (key.includes('sqft') || key.includes('area') || key.includes('size')) {
      return value.toLocaleString() + ' sq ft';
    }
    return value.toLocaleString();
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
}

/** Detect if a string value is a URL */
function isUrl(value: unknown): value is string {
  return typeof value === 'string' && /^https?:\/\//.test(value.trim());
}

/* Keys to skip in the detail view (internal/partition keys) */
const SKIP_KEYS = new Set(['fips_state', 'composite_key']);

/* ── Drawer component ── */

function ParcelDrawer({ parcel, onClose }: { parcel: Parcel; onClose: () => void }) {
  const { data: sourceInfo } = useRecordSource('parcels', parcel.id);
  const refreshMutation = useRefreshRecord();
  const acceptMutation = useAcceptChanges();
  const [refreshResult, setRefreshResult] = useState<{ diff: FieldChange[]; status: string } | null>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleRefresh = async () => {
    const result = await refreshMutation.mutateAsync({ registry: 'parcels', recordId: parcel.id });
    setRefreshResult(result);
  };

  const entries = Object.entries(parcel).filter(
    ([key, value]) => !SKIP_KEYS.has(key) && value !== null && value !== undefined && value !== ''
  );

  const groups: { label: string; keys: string[] }[] = [
    { label: 'Identification', keys: ['id', 'fips5', 'apn_raw', 'apn_normalized', 'upi', 'composite_key'] },
    { label: 'Location', keys: ['address_full', 'address_line1', 'address_line2', 'city', 'state_abbr', 'zip5', 'zip9', 'county_name', 'country', 'latitude', 'longitude', 'census_tract', 'census_block'] },
    { label: 'Owner', keys: ['owner_name', 'owner_type', 'owner_address', 'owner_city', 'owner_state', 'owner_zip', 'mailing_address'] },
    { label: 'Property', keys: ['land_use_category', 'land_use_code', 'property_class', 'zoning', 'year_built', 'building_sqft', 'lot_size_sqft', 'lot_size_acres', 'bedrooms', 'bathrooms', 'stories', 'units'] },
    { label: 'Valuation', keys: ['assessed_value_total', 'assessed_value_land', 'assessed_value_improvement', 'market_value_total', 'market_value_land', 'market_value_improvement', 'tax_amount', 'tax_year', 'sale_price', 'sale_date'] },
    { label: 'Source', keys: ['source_type', 'source_id', 'crawl_run_id', 'created_at', 'updated_at'] },
  ];

  const usedKeys = new Set(groups.flatMap(g => g.keys));
  const extraEntries = entries.filter(([key]) => !usedKeys.has(key));

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 animate-fade-in"
        onClick={onClose}
      />
      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 w-full max-w-lg z-50 flex">
        <div
          className="w-full border-l border-border overflow-y-auto shadow-[-4px_0_16px_rgba(0,0,0,0.12)]"
          style={{ background: getTheme() === 'dark' ? '#18181b' : 'linear-gradient(225deg, oklch(72% 0.14 240) 0%, oklch(80% 0.08 225) 100%)', animation: 'slide-in-right 0.2s ease-out' }}
        >
          {/* Header */}
          <div className="sticky top-0 bg-background/95 backdrop-blur-sm border-b border-border p-4 flex items-center justify-between z-10">
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-blue-900 dark:text-blue-400 truncate">
                {parcel.address_full || parcel.apn_raw || `Parcel #${parcel.id}`}
              </h2>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-sm text-muted-foreground">
                  {[parcel.city, parcel.state_abbr?.trim(), parcel.zip5?.trim()].filter(Boolean).join(', ')}
                </p>
                {sourceInfo?.found && sourceInfo.source && (
                  <FreshnessBadge
                    lastVerifiedAt={sourceInfo.source.lastVerifiedAt}
                    status={sourceInfo.source.lastRefreshStatus}
                  />
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {sourceInfo?.found && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleRefresh}
                  disabled={refreshMutation.isPending}
                  title="Check Latest"
                >
                  <RefreshCw className={`h-4 w-4 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
                </Button>
              )}
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Body: grouped key-value pairs */}
          <div className="p-4 space-y-6">
            {groups.map(group => {
              const groupEntries = entries.filter(([key]) => group.keys.includes(key));
              if (groupEntries.length === 0) return null;
              return (
                <div key={group.label}>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-blue-900 dark:text-blue-400 mb-2">
                    {group.label}
                  </h3>
                  <div className="rounded-lg border border-border divide-y divide-border">
                    {groupEntries.map(([key, value]) => (
                      <div key={key} className="flex gap-4 px-3 py-2 text-sm">
                        <span className="font-bold text-muted-foreground w-40 shrink-0 truncate" title={formatKey(key)}>
                          {formatKey(key)}
                        </span>
                        <span className="font-mono text-xs break-all">
                          {isUrl(value)
                            ? <a href={value.trim()} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 underline">{value.trim()}</a>
                            : formatValue(key, value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Extra fields not in any group */}
            {extraEntries.length > 0 && (
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-blue-900 dark:text-blue-400 mb-2">
                  Additional Data
                </h3>
                <div className="rounded-lg border border-border divide-y divide-border">
                  {extraEntries.map(([key, value]) => (
                    <div key={key} className="flex gap-4 px-3 py-2 text-sm">
                      <span className="font-bold text-muted-foreground w-40 shrink-0 truncate" title={formatKey(key)}>
                        {formatKey(key)}
                      </span>
                      <span className="font-mono text-xs break-all">
                        {isUrl(value)
                          ? <a href={value.trim()} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 underline">{value.trim()}</a>
                          : formatValue(key, value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Change Report (shown after refresh) */}
            {refreshResult && refreshResult.diff.length > 0 && (
              <ChangeReport
                diff={refreshResult.diff}
                onAcceptAll={() => {
                  // Accept changes would need changeIds from the API — for now accept all pending
                  // This would be enhanced with actual change IDs from the refresh response
                }}
                accepting={acceptMutation.isPending}
              />
            )}
            {refreshResult && refreshResult.diff.length === 0 && refreshResult.status === 'unchanged' && (
              <div className="text-center py-3 text-xs text-emerald-600 dark:text-emerald-400">
                Data is up to date — no changes detected
              </div>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

/* ── Main page ── */

export default function ParcelsPage() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [page, setPage] = useState(1);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [selectedParcel, setSelectedParcel] = useState<Parcel | null>(null);
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
      setPage(1);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading, isFetching } = useParcels({
    q: debouncedSearch || undefined,
    state: stateFilter || undefined,
    page,
    sort: sortField || undefined,
    order: sortField ? sortDir : undefined,
  });

  const handleClose = useCallback(() => setSelectedParcel(null), []);

  return (
    <div className="space-y-6 animate-fade-up p-1">
      <PageBanner>
        <div className="flex items-center justify-between">
          <div className="banner-chip">
            <h1 className="text-2xl font-bold banner-title">Parcels</h1>
          </div>
          <div className="banner-chip">
            {isFetching && <Loader2 className="h-4 w-4 animate-spin" />}
          </div>
        </div>

        {/* Search Bar */}
        <div className="flex gap-3 items-center">
          <div className="banner-chip flex-1">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search address, APN, owner, or UPI..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 pr-9 h-11 text-base bg-background/90"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          <div className="banner-chip">
            <label className="text-sm font-medium text-muted-foreground">State</label>
            <Input
              placeholder="ST"
              value={stateFilter}
              onChange={e => { setStateFilter(e.target.value.toUpperCase().slice(0, 2)); setPage(1); }}
              className="w-20 h-11 bg-background/90"
            />
          </div>
        </div>
      </PageBanner>

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <MapPin className="h-5 w-5 text-blue-700 dark:text-blue-400" />
            <span className="text-blue-700 dark:text-blue-400">Parcel Records</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <GlobeSpinner label="Searching parcels..." />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[260px]"><Fingerprint className="h-3.5 w-3.5 inline mr-1" />UPI</TableHead>
                    <TableHead>APN</TableHead>
                    <TableHead>
                      <SortableHeader label="Address" field="address_full" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="City" field="city" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="St" field="state_abbr" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Owner" field="owner_name" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Use" field="land_use_category" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead className="text-right">
                      <SortableHeader label="Assessed" field="assessed_value_total" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead className="text-right">
                      <SortableHeader label="Yr Built" field="year_built" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.parcels.map((p, idx) => (
                    <TableRow
                      key={p.id}
                      className={`cursor-pointer hover:bg-accent/50 ${idx % 2 === 0 ? '' : 'bg-table-row-even'}`}
                      onClick={() => setSelectedParcel(p)}
                    >
                      <TableCell className="font-mono text-xs w-[260px]">
                        {p.upi ? (
                          <div className="flex items-center gap-2">
                            <span className="truncate max-w-[200px]" title={`0x${p.upi}`}>0x{p.upi.slice(0, 18)}...</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0 shrink-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                void navigator.clipboard.writeText(`0x${p.upi!}`);
                                setCopiedId(p.id);
                                setTimeout(() => setCopiedId(null), 1500);
                              }}
                              title="Copy UPI"
                            >
                              {copiedId === p.id
                                ? <Check className="h-3.5 w-3.5 text-green-500" />
                                : <Copy className="h-3.5 w-3.5" />}
                            </Button>
                          </div>
                        ) : <span className="text-muted-foreground">-</span>}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{p.apn_raw || p.apn_normalized || '-'}</TableCell>
                      <TableCell className="max-w-[200px] truncate" title={p.address_full ?? ''}>
                        {p.address_full || '-'}
                      </TableCell>
                      <TableCell>{p.city || '-'}</TableCell>
                      <TableCell className="font-mono">{p.state_abbr?.trim() || '-'}</TableCell>
                      <TableCell className="max-w-[150px] truncate" title={p.owner_name ?? ''}>
                        {p.owner_name || '-'}
                      </TableCell>
                      <TableCell>
                        {p.land_use_category
                          ? <Badge variant={p.land_use_category.toLowerCase().includes('resident') ? 'success' : 'info'} className="text-xs">{p.land_use_category}</Badge>
                          : '-'}
                      </TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(p.assessed_value_total)}</TableCell>
                      <TableCell className="text-right font-mono">{p.year_built || '-'}</TableCell>
                    </TableRow>
                  ))}
                  {data?.parcels.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-16">
                        <div className="flex flex-col items-center gap-3">
                          <Search className="h-10 w-10 text-muted-foreground/40" />
                          <div>
                            <p className="font-medium text-muted-foreground">No parcels found</p>
                            <p className="text-sm text-muted-foreground/70 mt-1">Try adjusting your search terms or filters</p>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {(data?.parcels.length ?? 0) > 0 && (
                <TablePager
                  page={page}
                  onPageChange={setPage}
                  total={data?.total}
                  pageSize={data?.limit ?? 20}
                  hasMore={data?.hasMore}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      {selectedParcel && <ParcelDrawer parcel={selectedParcel} onClose={handleClose} />}
    </div>
  );
}
